import { useCallback, useEffect, useMemo, useState } from 'react';
import './App.css';
import LoadingIndicator from './LoadingIndicator';
import AppSidebar from './AppSidebar';
import { getInitials } from './tasklist';
import { getUserColor } from './colorUtils';

function formatLogTime(value) {
  if (!value) return '-';
  const parsed = new Date(String(value).replace(' ', 'T'));
  if (Number.isNaN(parsed.getTime())) return value;
  const day = String(parsed.getDate()).padStart(2, '0');
  const month = parsed.toLocaleString('en-US', { month: 'short' });
  const time = parsed.toLocaleString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true });
  return `${day} ${month}, ${time.toLowerCase()}`;
}

function humanizeField(field) {
  return String(field || '')
    .replace(/_/g, ' ')
    .replace(/\b\w/g, (char) => char.toUpperCase());
}

function valueText(value) {
  if (value === null || value === undefined || value === '') return 'unset';
  if (typeof value === 'object') return JSON.stringify(value);
  return String(value);
}

function summarizeLog(log) {
  const details = log.details || {};
  const changes = Array.isArray(details.changed_fields) ? details.changed_fields : [];

  if (changes.length === 1) {
    const change = changes[0];
    return `${humanizeField(change.field)}: ${valueText(change.from)} -> ${valueText(change.to)}`;
  }

  if (changes.length > 1) {
    return `${log.action}: changed ${changes.length} fields`;
  }

  if (details.spawned_task_id) {
    return `${log.action}: created next task ${details.spawned_task_id}`;
  }

  if (details.from_status || details.to_status) {
    return `Status: ${valueText(details.from_status)} -> ${valueText(details.to_status)}`;
  }

  if (details.from_recurring_active !== undefined || details.to_recurring_active !== undefined) {
    const from = Number(details.from_recurring_active) === 1 ? 'active' : 'stopped';
    const to = Number(details.to_recurring_active) === 1 ? 'active' : 'stopped';
    return `Recurring: ${from} -> ${to}`;
  }

  if (details.before && details.after) {
    return log.action;
  }

  return log.action;
}

function getChangeRows(log) {
  const details = log.details || {};
  const changes = Array.isArray(details.changed_fields) ? details.changed_fields : [];

  if (changes.length) {
    return changes.map((change) => ({
      field: humanizeField(change.field),
      from: valueText(change.from),
      to: valueText(change.to),
    }));
  }

  if (details.from_status || details.to_status) {
    return [{ field: 'Status', from: valueText(details.from_status), to: valueText(details.to_status) }];
  }

  if (details.from_recurring_active !== undefined || details.to_recurring_active !== undefined) {
    return [{
      field: 'Recurring',
      from: Number(details.from_recurring_active) === 1 ? 'active' : 'stopped',
      to: Number(details.to_recurring_active) === 1 ? 'active' : 'stopped',
    }];
  }

  if (details.spawned_task_id) {
    return [{ field: 'Spawned Task', from: 'unset', to: valueText(details.spawned_task_id) }];
  }

  if (details.before?.active !== undefined || details.after?.active !== undefined) {
    return [{ field: 'Active', from: valueText(details.before?.active), to: valueText(details.after?.active) }];
  }

  return [{ field: '-', from: '-', to: '-' }];
}

function ExtraDetails({ log }) {
  const details = log.details || {};
  const omitted = { ...details };
  delete omitted.changed_fields;
  delete omitted.before;
  delete omitted.after;
  delete omitted.from_status;
  delete omitted.to_status;
  delete omitted.from_recurring_active;
  delete omitted.to_recurring_active;

  const entries = Object.entries(omitted).filter(([, value]) => value !== undefined && value !== null && value !== '');
  if (!entries.length) return <span className="logs-extra-empty">No extra metadata</span>;

  return entries.map(([key, value]) => (
    <span className="logs-extra-chip" key={`${log.id}-${key}`}>
      {humanizeField(key)}: <strong>{valueText(value)}</strong>
    </span>
  ));
}

function Logging({
  user,
  onAuthError,
  onLogout,
  loggingOut,
  onNavigateToDashboard,
  onNavigateToTasks,
  onNavigateToFMS,
  onNavigateToReturns,
  onNavigateToDelegation,
  onNavigateToAllTasks,
  onNavigateToResourceControl,
  onNavigateToLogging,
}) {
  const [logs, setLogs] = useState([]);
  const [sections, setSections] = useState([]);
  const [section, setSection] = useState('');
  const [search, setSearch] = useState('');
  const [taskId, setTaskId] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [expandedLogId, setExpandedLogId] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const loadLogs = useCallback(async () => {
    setLoading(true);
    setError('');

    try {
      const params = new URLSearchParams();
      if (section) params.set('section', section);
      if (search) params.set('search', search);
      if (taskId) params.set('task_id', taskId);
      if (dateFrom) params.set('date_from', dateFrom);
      if (dateTo) params.set('date_to', dateTo);

      const res = await fetch(`/api/logs?${params.toString()}`, { credentials: 'include' });
      const data = await res.json().catch(() => ({}));
      if (res.status === 401) {
        onAuthError();
        return;
      }
      if (!res.ok) throw new Error(data.message || 'Unable to load logs');

      const nextLogs = Array.isArray(data.logs) ? data.logs : [];
      setLogs(nextLogs);
      setSections(Array.isArray(data.sections) ? data.sections : []);
      setExpandedLogId((prev) => (nextLogs.some((item) => item.id === prev) ? prev : null));
    } catch (err) {
      setError(err.message || 'Unable to load logs');
      setLogs([]);
      setExpandedLogId(null);
    } finally {
      setLoading(false);
    }
  }, [dateFrom, dateTo, onAuthError, search, section, taskId]);

  useEffect(() => {
    loadLogs();
  }, [loadLogs]);

  const sectionOptions = useMemo(
    () => sections.map((item) => ({ section: item.section, count: Number(item.count || 0) })),
    [sections]
  );

  return (
    <div className="app-shell">
      <AppSidebar
        user={user}
        active="logging"
        onLogout={onLogout}
        loggingOut={loggingOut}
        onNavigateToDashboard={onNavigateToDashboard}
        onNavigateToTasks={onNavigateToTasks}
        onNavigateToFMS={onNavigateToFMS}
        onNavigateToReturns={onNavigateToReturns}
        onNavigateToDelegation={onNavigateToDelegation}
        onNavigateToAllTasks={onNavigateToAllTasks}
        onNavigateToResourceControl={onNavigateToResourceControl}
        onNavigateToLogging={onNavigateToLogging}
      />

      <main className="workspace">
        <header className="topbar">
          <div className="mobile-left">
            <button className="mobile-menu-btn" type="button" onClick={() => setMobileMenuOpen(true)} />
          </div>
          <div className="topbar-actions">
            <div className="avatar-placeholder" style={{ background: getUserColor(user?.emp_name || user?.username) }}>
              {getInitials(user)}
            </div>
          </div>
        </header>

        <section className="logs-shell">
          <div className="logs-header">
            <div>
              <h2>Logging</h2>
              <p>{logs.length} recent events</p>
            </div>
            <button className="tool-btn" type="button" onClick={loadLogs} disabled={loading}>
              Refresh
            </button>
          </div>

          <div className="logs-filters">
            <input type="search" value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search action, section, user, path" />
            <input type="search" value={taskId} onChange={(e) => setTaskId(e.target.value)} placeholder="Task ID" />
            <select value={section} onChange={(e) => setSection(e.target.value)}>
              <option value="">All sections</option>
              {sectionOptions.map((item) => (
                <option key={item.section} value={item.section}>{item.section}</option>
              ))}
            </select>
            <input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} />
            <input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} />
          </div>

          <div className="logs-section-grid">
            <button className={`logs-section-card ${section === '' ? 'active' : ''}`} type="button" onClick={() => setSection('')}>
              <span>All</span>
              <strong>{sectionOptions.reduce((sum, item) => sum + item.count, 0)}</strong>
            </button>
            {sectionOptions.map((item) => (
              <button
                key={item.section}
                className={`logs-section-card ${section === item.section ? 'active' : ''}`}
                type="button"
                onClick={() => setSection(item.section)}
              >
                <span>{item.section}</span>
                <strong>{item.count}</strong>
              </button>
            ))}
          </div>

          {error ? <div className="form-error">{error}</div> : null}

          <div className="logs-feed">
              {!loading && logs.length ? (
                <div className="logs-table-head">
                  <span>Time</span>
                  <span>User</span>
                  <span>Section</span>
                  <span>Task ID</span>
                  <span>Action</span>
                  <span>Field</span>
                  <span>Old Value</span>
                  <span />
                  <span>New Value</span>
                  <span />
                </div>
              ) : null}
              {loading ? (
                <LoadingIndicator variant="panel" label="Loading logs..." />
              ) : (
                logs.length ? logs.map((log) => {
                  const expanded = expandedLogId === log.id;
                  const changeRows = getChangeRows(log);
                  return (
                    <article className="logs-table-event" key={log.id}>
                      {changeRows.map((change, index) => (
                        <div className="logs-table-row" key={`${log.id}-${change.field}-${index}`}>
                          <time>{index === 0 ? formatLogTime(log.createdAt) : ''}</time>
                          <strong className="logs-actor">{index === 0 ? (log.actorName || '-') : ''}</strong>
                          <span className={index === 0 ? 'logs-badge' : 'logs-badge ghost'}>{index === 0 ? log.section : ''}</span>
                          <span className="logs-task-id">{index === 0 ? (log.taskId || log.entityId || '-') : ''}</span>
                          <span className="logs-action-name">{index === 0 ? log.action : ''}</span>
                          <span className="logs-field-name">{change.field}</span>
                          <span className="logs-change-value old">{change.from}</span>
                          <span className="logs-change-arrow">-></span>
                          <span className="logs-change-value new">{change.to}</span>
                          <span className="logs-row-actions">
                            {index === 0 && (
                              <button
                                type="button"
                                className="logs-details-toggle"
                                onClick={() => setExpandedLogId(expanded ? null : log.id)}
                              >
                                {expanded ? 'Hide' : 'Details'}
                              </button>
                            )}
                          </span>
                        </div>
                      ))}
                      {expanded && (
                        <div className="logs-table-extra">
                          <span>Summary: <strong>{summarizeLog(log)}</strong></span>
                          <span>Exact time: <strong>{log.createdAt}</strong></span>
                          <ExtraDetails log={log} />
                        </div>
                      )}
                    </article>
                  );
                }) : (
                  <div className="logs-empty">No logs found</div>
                )
              )}
          </div>
        </section>
      </main>

      {mobileMenuOpen && (
        <div className="mobile-nav-overlay" onClick={() => setMobileMenuOpen(false)}>
          <aside className="mobile-nav-sheet" onClick={(e) => e.stopPropagation()}>
            <nav className="mobile-nav-list">
              <button className="mobile-nav-item" onClick={() => onNavigateToDashboard && onNavigateToDashboard()}>Dashboard</button>
              <button className="mobile-nav-item" onClick={() => onNavigateToTasks && onNavigateToTasks()}>Tasks</button>
              <button className="mobile-nav-item active">Logging</button>
              <button className="mobile-nav-item" onClick={onLogout} disabled={loggingOut}>Logout</button>
            </nav>
          </aside>
        </div>
      )}
    </div>
  );
}

export default Logging;
